import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

import './landing.css';

// Marketing landing page for www.getstreamapp.com/.
//
// The markup and styles come from the standalone page design; they are kept
// here rather than on separate hosting so that this one Heroku app keeps
// serving every path on the domain. /reset, /user/email/verify, /verify/return
// and the /legal/* pages all live in this same app, and DNS cannot route a
// single hostname to two origins by path.
//
// landing.css is scoped under `html.landing-page` because the source stylesheet
// restyles bare `body`, `a`, `h1`, `p` and `img`. Without that scope it would
// leak onto the EULA, terms, privacy and password-reset screens. The class is
// added to <html> on mount and removed on unmount, so the scope is exact.

// Inter carries no glyph for U+2197 (the North East Arrow the design used),
// so the character fell through to whatever the platform supplied. U+2197 is
// emoji-capable, which meant iOS rendered it from Apple Color Emoji and the
// store buttons showed a blue emoji instead of the arrow. An inline SVG drops
// the font dependency entirely: 1em keeps it sized by the surrounding
// font-size rules and currentColor keeps it matching the text.
function ArrowUpRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 16L16 8" />
      <path d="M9.5 7.5H16.5V14.5" />
    </svg>
  );
}

function Landing() {
  const [navOpen, setNavOpen] = useState(false);
  const toggleRef = useRef(null);

  // Scope the stylesheet to this screen only.
  //
  // useLayoutEffect, not useEffect: every rule in landing.css is scoped under
  // html.landing-page, so the class has to be on <html> before the browser
  // paints. useEffect is deferred until after paint, which can show one frame
  // of the page with no styles applied at all.
  useLayoutEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  const closeNav = useCallback(() => setNavOpen(false), []);

  // Tapping any nav link closes the mobile menu.
  const handleNavClick = useCallback((event) => {
    if (event.target.closest('a')) closeNav();
  }, [closeNav]);

  // Escape closes the menu and returns focus to the toggle.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeNav();
        if (toggleRef.current) toggleRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen, closeNav]);

  // Leaving the mobile breakpoint closes the menu.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 701px)');
    const onChange = (event) => { if (event.matches) closeNav(); };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [closeNav]);

  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <header className="header wrap">
        <a className="brand" href="/" aria-label="Stream home">
          <img src="/assets/stream-logo.png" width="160" height="40" alt="Stream" />
        </a>
        <nav
          id="main-nav"
          className={navOpen ? 'is-open' : undefined}
          aria-label="Main navigation"
          onClick={handleNavClick}
        >
          <a href="#how-it-works">How it works</a>
          <a href="#why-stream">Why Stream</a>
          <a href="#faq">FAQs</a>
        </nav>
        <button
          ref={toggleRef}
          className="menu-toggle"
          type="button"
          aria-expanded={navOpen}
          aria-controls="main-nav"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          onClick={() => setNavOpen((open) => !open)}
        >
          <span>
          </span>
          <span>
          </span>
        </button>
        <a className="button header-download" href="#download">Get the app <span aria-hidden="true"><ArrowUpRight /></span>
      </a>
      </header>
      <main id="main">
        <section className="hero wrap">
          <div className="hero-copy">
            <p className="eyebrow">EARNED BY YOU. READY FOR LIFE.</p>
            <h1>Your pay. <br />Your <span className="highlight">timing.</span>
          </h1>
          <p className="lead">Life doesn’t wait for payday. Access the pay you’ve already earned and take care of what’s next.</p>
          <div className="store-links">
            <a className="store-button" href="https://apps.apple.com/us/app/get-paid-early-cash-advance/id6446709359">
              <span className="store-symbol" aria-hidden="true"><ArrowUpRight /></span>
              <span>
                <small>Download on the</small>
                <b>App Store</b>
              </span>
            </a>
            <a className="store-button" href="https://play.google.com/store/apps/details?id=com.flow9243.Stream">
              <span className="store-symbol" aria-hidden="true">▷</span>
              <span>
                <small>Get it on</small>
                <b>Google Play</b>
              </span>
            </a>
          </div>
          <p className="fine">Subject to approval. Available amounts and transfer times vary.</p>
          <div className="hero-proof">
            <span>No credit checks</span>
            <span>No interest</span>
            <span>No subscriptions</span>
          </div>
        </div>
        <div className="hero-product">
          <div className="product-orbit" aria-hidden="true">
          </div>
          <div className="hero-stamp">
            <span aria-hidden="true">✓</span>
            <b>Already yours. <br />Just earlier.</b>
          </div>
          <div className="dashboard-crop">
            <img src="/assets/app-1.webp" width="600" height="1300" alt="Stream app dashboard showing a $300 available advance, a withdrawal button and payment history. Illustrative amount; eligibility varies." />
          </div>
          <div className="hero-product-note">Your pay, a little more in reach.</div>
        </div>
      </section>
      <section className="benefit-band" aria-label="Stream benefits">
        <div className="wrap">
          <p>Already earned. <br />
          <b>Ready when life happens.</b>
        </p>
        <div>
          <strong>24/7</strong>
          <span>Access in the app</span>
        </div>
        <div>
          <strong>$0</strong>
          <span>Membership fees</span>
        </div>
        <div>
          <strong className="slashed-zero" aria-label="Zero">0</strong>
          <span>Tipping prompts</span>
        </div>
      </div>
      </section>
      <section className="section wrap intro" id="why-stream">
        <div>
          <p className="eyebrow">A LITTLE MORE ROOM TO BREATHE</p>
          <h2>Payday is a date. <br />
          <span className="soft">Life is every day.</span>
        </h2>
      </div>
      <p className="intro-copy">You’ve put in the hours. Stream helps you access part of that earned pay before payday—so the gap between work and getting paid doesn’t have to hold you back.</p>
      </section>
      <section className="life-section">
        <div className="wrap">
          <div className="life-heading">
            <p className="eyebrow">FOR THE DAYS BETWEEN PAYDAYS</p>
            <h2>A bill. A full fridge. <br />
            <span>Whatever’s next.</span>
          </h2>
          <p>More control over the money you’ve earned. <br />For the things that can’t wait.</p>
        </div>
        <div className="life-examples">
          <article>
            <span className="example-number">01</span>
            <h3>The bill that <br />landed early.</h3>
            <p>Handle an expense before your next paycheck hits.</p>
          </article>
          <article>
            <span className="example-number">02</span>
            <h3>The essentials <br />you need now.</h3>
            <p>Make room for groceries, gas, and the everyday.</p>
          </article>
          <article>
            <span className="example-number">03</span>
            <h3>The surprise <br />you didn’t plan for.</h3>
            <p>A little flexibility when life changes the plan.</p>
          </article>
        </div>
      </div>
      </section>
      <section className="section wrap how" id="how-it-works">
        <div className="section-header">
          <div>
            <p className="eyebrow">FROM SIGNUP TO EARLY PAY</p>
            <h2>A few steps. <br />Ahead of payday.</h2>
          </div>
          <p>Connect your work. Choose your transfer. <br />Stream takes care of repayment on payday.</p>
        </div>
        <ol className="steps">
          <li>
            <span>01</span>
            <h3>Make it yours.</h3>
            <p>Download Stream on iOS or Android and create your account.</p>
          </li>
          <li>
            <span>02</span>
            <h3>Connect your pay.</h3>
            <p>Find your employer or payroll provider, then connect your payroll and bank account.</p>
          </li>
          <li>
            <span>03</span>
            <h3>Choose your amount.</h3>
            <p>See what’s available, choose your delivery option, and review the details before you confirm.</p>
          </li>
          <li>
            <span>04</span>
            <h3>Settle on payday.</h3>
            <p>Repayment happens automatically on payday. Track your transfers and repayment in the app.</p>
          </li>
        </ol>
      </section>
      <section className="feature-section wrap">
        <div className="connection-art">
          <div className="employer-screen">
            <img loading="lazy" src="/assets/app-3.webp" width="600" height="1300" alt="Stream employer search screen with payroll providers including ADP and Workday, plus no credit check, interest, late fee or monthly fee benefits." />
          </div>
        </div>
        <div className="feature-copy">
          <p className="eyebrow">YOUR JOB IS THE CONNECTION</p>
          <h2>Your income. <br />
          <span className="soft">Not your <br />credit score.</span>
        </h2>
        <p>Stream connects to your payroll to help you access wages you’ve already earned. There’s no credit check and no paperwork-filled loan application.</p>
        <div className="feature-points">
          <div>
            <b>Find your employer in minutes.</b>
            <p>Search for your company or payroll provider right in the app.</p>
          </div>
          <div>
            <b>Keep the bank account you use.</b>
            <p>Send your pay to a linked bank account or eligible debit card.</p>
          </div>
          <div>
            <b>See your access grow.</b>
            <p>Your available amount may increase over time with qualifying activity. Check the app for your eligibility and access requirements.</p>
          </div>
        </div>
        <a className="text-link" href="#download">Find your employer in Stream <ArrowUpRight />
      </a>
      </div>
      </section>
      <section className="pricing-section" id="pricing">
        <div className="section wrap">
          <div className="section-header">
            <div>
              <p className="eyebrow">SIMPLE BY DESIGN</p>
              <h2>Your pay. <br />Without the extras.</h2>
            </div>
            <p>No interest. No late fees. No monthly membership. <br />And never a prompt to leave a tip.</p>
          </div>
          <div className="pricing-layout">
            <div className="pricing-options">
              <article className="delivery standard">
                <div className="delivery-heading">
                  <span className="eyebrow">STANDARD DELIVERY</span>
                  <span className="delivery-tag">The free option</span>
                </div>
                <div className="price">$0<span>transfer fee</span>
              </div>
              <h3>Keep every dollar <br />you withdraw.</h3>
              <p>Choose standard delivery to your linked bank account. See your estimated arrival time before you confirm.</p>
            </article>
            <article className="delivery instant">
              <div className="delivery-heading">
                <span className="eyebrow">INSTANT DELIVERY</span>
                <span className="delivery-tag">When you need it sooner</span>
              </div>
              <h3>Less waiting. <br />More moving.</h3>
              <p>Choose a faster transfer to an eligible debit card. An instant-delivery fee may apply—you’ll see the fee and the amount you’ll receive before confirming.</p>
              <span className="delivery-note">Timing and availability depend on your account and payment provider.</span>
            </article>
          </div>
          <figure className="success-art">
            <img loading="lazy" src="/assets/app-2.webp" width="600" height="1300" alt="Stream withdrawal confirmation showing a $300 withdrawal, zero fee and $300 received with standard delivery." />
            <figcaption>App screen shown for illustration. <br />Your available amount may differ.</figcaption>
          </figure>
        </div>
      </div>
      </section>
      <section className="section wrap faq" id="faq">
        <div className="faq-intro">
          <p className="eyebrow">GOOD QUESTIONS. CLEAR ANSWERS.</p>
          <h2>Before you <br />make a move.</h2>
          <p>Need a hand with your account? <br />We’re here to help.</p>
          <a className="text-link" href="mailto:support@getstreamapp.com">Contact support <ArrowUpRight />
        </a>
      </div>
      <div className="faq-list">
        <details>
          <summary>Is Stream a loan?</summary>
          <p>No. Stream is an earned wage access service: it gives you access to a portion of pay you’ve already earned before your scheduled payday. There are no interest charges or credit checks. The amount you access is repaid automatically on payday.</p>
        </details>
        <details>
          <summary>Does Stream cost anything?</summary>
          <p>Standard delivery to your linked bank account is free. Instant delivery may carry a fee, shown in the app before you confirm. There are no recurring membership fees, interest charges, late fees, or tips.</p>
        </details>
        <details>
          <summary>Will Stream work with my employer?</summary>
          <p>Stream works with many employers and payroll providers. Download the app and search for your employer or provider to check. Access depends on successful payroll connection, application review, and account eligibility.</p>
        </details>
        <details>
          <summary>How much can I access?</summary>
          <p>Your available amount is personalized and shown in the app. It depends on your eligibility and may change over time. The amounts pictured on this page are examples, not a guaranteed offer.</p>
        </details>
        <details>
          <summary>How quickly will my money arrive?</summary>
          <p>You can request access in the app 24/7. Transfer speed depends on the delivery option, your bank or card provider, and processing conditions. Review the estimated arrival time and any fee before confirming your transfer.</p>
        </details>
        <details>
          <summary>How do I qualify for higher access?</summary>
          <p>Higher access depends on qualifying repayment activity and your account’s requirements. Check the app’s upgrade information before withdrawing; not every transfer or repayment counts toward a higher access level. Increases aren’t guaranteed.</p>
        </details>
        <details>
          <summary>Where is Stream available?</summary>
          <p>Stream is available in the United States on iOS and Android. Look for “Get Paid Early: Cash Advance” by StreamFunds, Inc. in the App Store or Google Play.</p>
        </details>
      </div>
      </section>
      <section className="download wrap" id="download">
        <div className="download-panel">
          <div>
            <p className="eyebrow">YOU DID THE WORK.</p>
            <h2>Make your pay <br />
            <span>work for you.</span>
          </h2>
          <p>More flexibility starts with Stream.</p>
          <div className="store-links">
            <a className="store-button" href="https://apps.apple.com/us/app/get-paid-early-cash-advance/id6446709359">
              <span className="store-symbol" aria-hidden="true"><ArrowUpRight /></span>
              <span>
                <small>Download on the</small>
                <b>App Store</b>
              </span>
            </a>
            <a className="store-button" href="https://play.google.com/store/apps/details?id=com.flow9243.Stream">
              <span className="store-symbol" aria-hidden="true">▷</span>
              <span>
                <small>Get it on</small>
                <b>Google Play</b>
              </span>
            </a>
          </div>
        </div>
        <div className="download-wordmark" aria-hidden="true">
          <img src="/assets/stream-icon.png" width="250" height="256" alt="" />
          <span>Less waiting. <br />More living.</span>
        </div>
      </div>
      </section>
      </main>
      <footer className="wrap footer">
        <div className="footer-top">
          <a className="brand" href="/" aria-label="Stream home">
            <img src="/assets/stream-logo.png" width="160" height="40" alt="Stream" />
          </a>
          <div className="footer-links">
            <a href="mailto:support@getstreamapp.com">Support</a>
            <a href="/terms">Terms & policies</a>
            <a href="/legal/privacy">Privacy</a>
            <a href="https://rent.getstreamapp.com">Stream Rent <ArrowUpRight /></a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>Stream is a financial services platform, not a bank. Available in the U.S. only. Approval, available amounts, employer compatibility, and transfer times vary. Standard delivery is free; instant-delivery fees may apply. Review the app for current terms.</p>
          <span>© 2026 StreamFunds, Inc.</span>
        </div>
      </footer>
    </>
  );
}

export default Landing;
